import { memo, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiIcon, MiIconButton } from "@miplatform/components";
import { addToDate, formatDate } from "@miplatform/utils";
import { mdiChevronDown } from "@mdi/js";
import { Box, Menu, Typography } from "@mui/material";

const MAX_BAR_WIDTH = 101;

const calculateBarWidth = (percentage = 0, maxBarWidth = MAX_BAR_WIDTH) => {
  const width = percentage * maxBarWidth;

  return width > maxBarWidth ? maxBarWidth : width;
};

/**
 * CapacityTracker Component
 *
 * Displays capacity tracking information for a selected order template, including approval rates and capacity limits.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Object} props.capacity - The current capacity data.
 * @param {string} props.capacity.date - The date of the capacity record.
 * @param {number} props.capacity.approved - The number of approved items.
 * @param {number} props.capacity.new - The number of new items.
 * @param {number} props.capacity.rescheduled - The number of rescheduled items.
 * @param {Object} [props.selectedOrderTemplate] - The currently selected ordet template.
 * @param {string} [props.selectedOrderTemplate.id] - The selected product ID.
 * @param {Array} props.orderTemplateCapacities - List of product capacities.
 * @param {string} props.orderTemplateCapacities[].orderTemplateId- The product ID.
 * @param {string} props.orderTemplateCapacities[].productName - The product name.
 * @param {number} props.orderTemplateCapacities[].approved - The approved capacity count.
 * @param {number} props.orderTemplateCapacities[].capacity - The total capacity.
 * @returns {JSX.Element} The rendered CapacityTracker component.
 */
export const CapacityTracker = memo(
  ({ capacity, selectedOrderTemplate, orderTemplateCapacities }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const { approved, date } = capacity;
    const { dayOfWeek, day, month } = useMemo(() => {
      const updatedDate = addToDate(date, 6, "hours");
      const dayOfWeek = formatDate(updatedDate, "dddd");
      const month = formatDate(updatedDate, "MM");
      const day = formatDate(updatedDate, "DD");

      return { dayOfWeek, day, month };
    }, [date]);

    const selectedCapacity = useMemo(() => {
      const selectedOrderTemplateCapacity = orderTemplateCapacities?.find(
        ({ orderTemplateId }) => orderTemplateId === selectedOrderTemplate?.id
      );
      if (selectedOrderTemplateCapacity) {
        const {
          new: newTotal,
          approved,
          rescheduled,
          capacity,
        } = selectedOrderTemplateCapacity;
        const { greenBarValue, blueBarValue } =
          capacity === 0
            ? {
                greenBarValue: approved >= 1 ? 1 : 0,
                blueBarValue: rescheduled + newTotal >= 1 ? 1 : 0,
              }
            : {
                greenBarValue: approved / capacity,
                blueBarValue: (rescheduled + newTotal + approved) / capacity,
              };

        return {
          newTotal,
          approved,
          rescheduled,
          capacity,
          greenBarValue,
          blueBarValue,
          isFull: greenBarValue >= 1,
          isOrange: approved === capacity && capacity !== 0,
        };
      }

      return {
        newTotal: 0,
        approved: 0,
        rescheduled: 0,
        capacity: 0,
        greenBarValue: 0,
        blueBarValue: 0,
        isFull: false,
        isOrange: false,
      };
    }, [selectedOrderTemplate, orderTemplateCapacities]);
    const { newTotal, rescheduled } = useMemo(() => {
      if (selectedOrderTemplate) {
        return {
          newTotal: selectedCapacity.newTotal,
          rescheduled: selectedCapacity.rescheduled,
        };
      }

      return { newTotal: capacity.new, rescheduled: capacity.rescheduled };
    }, [selectedOrderTemplate, selectedCapacity]);
    const { classes } = useStyles({
      isFull: selectedCapacity.isFull,
      isOrange: selectedCapacity.isOrange,
    });

    const handleMenuOpen = ({ currentTarget }) => setAnchorEl(currentTarget);

    const handleMenuClose = () => setAnchorEl(null);

    const renderOrderTemplatesCapacities = () =>
      orderTemplateCapacities?.map(
        ({ orderTemplateId, orderTemplateShortName, approved, capacity }) => (
          <Box key={orderTemplateId} className={classes.menuItem}>
            <Typography noWrap className={classes.text} width={70}>
              {orderTemplateShortName}
            </Typography>
            <Box className={classes.flexRow}>
              <Typography
                className={classes.text}
                color={approved >= capacity ? "error.main" : undefined}
              >
                {approved}
              </Typography>
              <Typography display="flex" className={classes.limitText}>
                &nbsp;{`/ ${capacity}`}
              </Typography>
            </Box>
          </Box>
        )
      );

    return (
      <Box className={classes.content}>
        <Box className={classes.contentRow}>
          <Typography className={classes.text}>{dayOfWeek}</Typography>
          <Typography className={classes.text}>{`${month}/${day}`}</Typography>
        </Box>
        <Box className={classes.middleContent}>
          {selectedOrderTemplate ? (
            <Box className={classes.flexRow}>
              <Typography className={classes.totalText}>
                {selectedCapacity?.approved}
              </Typography>
              <Typography display="flex" className={classes.limitText}>
                &nbsp;/ {selectedCapacity?.capacity}
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                className={classes.chevronContainer}
                onClick={handleMenuOpen}
              >
                <Typography className={classes.middleText}>
                  {approved}
                </Typography>
                {orderTemplateCapacities && (
                  <MiIconButton
                    data-test-id={`${dayOfWeek}-${day}-${month}-capacity-tracker-dropdown-btn`}
                    className={classes.dropDownIconButton}
                  >
                    <MiIcon path={mdiChevronDown} />
                  </MiIconButton>
                )}
              </Box>
              {orderTemplateCapacities && (
                <Menu
                  id="order-types-config-menu"
                  open={anchorEl}
                  anchorEl={anchorEl}
                  MenuListProps={{ className: classes.menuList }}
                  slotProps={{
                    paper: { className: classes.menuPaper, maxHeight: 300 },
                  }}
                  onClose={handleMenuClose}
                >
                  {renderOrderTemplatesCapacities()}
                </Menu>
              )}
            </>
          )}
        </Box>
        <Box className={classes.barsContainer}>
          {selectedOrderTemplate && <Box className={classes.mainBar} />}
          {!selectedCapacity.isFull && selectedOrderTemplate && (
            <>
              <Box
                className={classes.bar}
                sx={(theme) => ({
                  backgroundColor: theme.palette.blue.dark,
                  maxWidth: calculateBarWidth(selectedCapacity.blueBarValue),
                })}
              />
              <Box
                className={classes.bar}
                sx={(theme) => ({
                  backgroundColor: theme.palette.green.main,
                  maxWidth: calculateBarWidth(selectedCapacity.greenBarValue),
                })}
              />
            </>
          )}
        </Box>
        <Box className={classes.contentRow}>
          <Typography
            className={classes.bottomText}
            sx={{ color: newTotal > 0 ? "blue.dark" : "grey.medium" }}
          >
            {`New: ${newTotal}`}
          </Typography>
          <Typography
            className={classes.bottomText}
            sx={{ color: rescheduled > 0 ? "blue.dark" : "grey.medium" }}
          >
            {`Resch: ${rescheduled}`}
          </Typography>
        </Box>
      </Box>
    );
  }
);

const useStyles = makeStyles()((theme, { isFull, isOrange }) => ({
  content: {
    width: 119,
    height: 86,
    padding: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    backgroundColor: theme.palette.common.white,
    border: `2px solid ${theme.palette.grey.accent}`,
    borderTop: "none",
    borderLeft: "none",
  },
  contentRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(0, 1, 1, 1),
  },
  middleContent: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(0, 1, 1, 1),
  },
  chevronContainer: {
    display: "flex",
    cursor: "pointer",
  },
  flexRow: {
    display: "flex",
    alignItems: "center",
  },
  barsContainer: {
    position: "relative",
    minHeight: 5,
    paddingLeft: theme.spacing(1),
  },
  bar: {
    position: "absolute",
    height: 5,
    width: MAX_BAR_WIDTH,
    borderRadius: theme.spacing(0.3),
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  mainBar: {
    position: "absolute",
    height: 5,
    width: MAX_BAR_WIDTH,
    borderRadius: theme.spacing(0.3),
    backgroundColor: (() => {
      if (isOrange) {
        return theme.palette.warning.main;
      }

      if (isFull) {
        return theme.palette.error.main;
      }

      return theme.palette.grey.accent;
    })(),
  },
  text: {
    fontSize: theme.fontSize.small,
    fontWeight: theme.fontWeight.semiBold,
  },
  middleText: {
    fontSize: theme.fontSize.xlarge,
    fontWeight: theme.fontWeight.semiBold,
  },
  bottomText: {
    fontSize: theme.fontSize.small,
    fontWeight: theme.fontWeight.regular,
  },
  totalText: {
    fontSize: theme.fontSize.xlarge,
    fontWeight: theme.fontWeight.semiBold,
    color: (() => {
      if (isOrange) {
        return theme.palette.warning.main;
      }

      if (isFull) {
        return theme.palette.error.main;
      }

      return theme.palette.success.main;
    })(),
  },
  limitText: {
    fontSize: theme.fontSize.small,
    fontWeight: theme.fontWeight.regular,
    color: theme.palette.grey.medium,
  },
  menuPaper: {
    padding: theme.spacing(2),
    width: 125,
    maxHeight: 128,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  menuList: {
    padding: 0,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  menuItem: {
    display: "flex",
    justifyContent: "space-between",
  },
  dropDownIconButton: {
    width: 25,
    height: 25,
    padding: 0,
    color: theme.palette.grey.main,
  },
  dropDownIcon: {
    color: theme.palette.grey.main,
  },
}));
